import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./thankyou.css";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { ThemeIconImg } from "../../components/ThemeIconImg";
import { s3PublicUrl } from "../../utils/s3PublicUrl";
import { isMobileAppMode, mobileNavigateBack } from "../../utils/mobileAppMode";

/** Thank-you copy comes from themeset/API as plain strings only (no score-band objects). */
function themesetThankYouLine(val) {
  return typeof val === "string" ? val : "";
}

/** Display label for theme category keys (e.g. FINANCE → Finance). */
function formatCategoryHeading(key) {
  const s = String(key || "");
  return s
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Theme fields from themeset / API (text_color, box_*, button_*). */
function thankYouThemeVisuals(themeData) {
  const t = themeData || {};
  const c = t.colors && typeof t.colors === "object" && !Array.isArray(t.colors) ? t.colors : {};
  const root = (snake, camel) => {
    const v = t[snake] ?? t[camel];
    if (v != null && String(v).trim() !== "") return String(v).trim();
    return null;
  };
  const col = (snake, camel) => {
    const v = c[snake] ?? c[camel];
    if (v != null && String(v).trim() !== "") return String(v).trim();
    return null;
  };
  const textColor =
    root("text_color", "textColor") || col("text_color", "textColor") || "#0f172a";
  const boxTextColor =
    root("box_text_color", "boxTextColor") || col("box_text_color", "boxTextColor") || textColor;
  const boxBgColor =
    root("box_bg_color", "boxBgColor") ||
    col("box_bg_color", "boxBgColor") ||
    root("main_color_box", "mainColorBox") ||
    col("main_color_box", "mainColorBox") ||
    "rgba(0, 0, 0, 0.06)";
  const popupBgColor =
    root("popup_color", "popupColor") ||
    col("popup_color", "popupColor") ||
    boxBgColor;

  return {
    textColor,
    boxTextColor,
    cardBg: boxBgColor,
    popupBg: popupBgColor,
    /** @deprecated use textColor */
    labelColor: textColor,
    // Keep Thank You page colors only from themesets.js (box/text fields).
    meterBarColors: [textColor, textColor, textColor],
    submitBtnBg: boxBgColor,
    submitBtnText: boxTextColor,
  };
}

function parseHexColor(s) {
  const m = String(s || "").trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return { r, g, b };
}

function relLuminance({ r, g, b }) {
  const toLin = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const R = toLin(r);
  const G = toLin(g);
  const B = toLin(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatio(fgHex, bgHex) {
  const fg = parseHexColor(fgHex);
  const bg = parseHexColor(bgHex);
  if (!fg || !bg) return null;
  const L1 = relLuminance(fg);
  const L2 = relLuminance(bg);
  const hi = Math.max(L1, L2);
  const lo = Math.min(L1, L2);
  return (hi + 0.05) / (lo + 0.05);
}

function ensureReadableAccent(accentHex, bgHex, fallbackHex) {
  const cr = contrastRatio(accentHex, bgHex);
  if (cr == null) return fallbackHex;
  return cr >= 2.6 ? accentHex : fallbackHex;
}

/** Avoid re-showing feedback CTA after refresh once feedback was saved for this stage. */
function thankYouFeedbackStorageKey(userId, stageId) {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId).trim() : "";
  const sid = stageId != null && String(stageId).trim() !== "" ? String(stageId).trim() : "";
  if (!uid || !sid) return null;
  return `hs_thankyou_feedback_done_v1_${uid}_${sid}`;
}

function readThankYouFeedbackSubmitted(userId, stageId) {
  const key = thankYouFeedbackStorageKey(userId, stageId);
  if (!key || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeThankYouFeedbackSubmitted(userId, stageId) {
  const key = thankYouFeedbackStorageKey(userId, stageId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* quota / private mode */
  }
}

function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const state = location.state || {};
  const fromArrowRun = Boolean(state.fromArrowRun);
  const [rating, setRating] = useState(3);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const stageId = useMemo(() => {
    const s = state.stageId;
    if (s != null && s !== "") {
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }, [state.stageId]);

  useEffect(() => {
    if (readThankYouFeedbackSubmitted(user?.userId, stageId)) {
      setFeedbackSubmitted(true);
    }
  }, [user?.userId, stageId]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const isFutureIndia = themeData?.gameType === 'future_india';

  /** Exit URL: parent portal / org — never in-app game routes (avoids replay). */
  const safeExitUrl = useMemo(() => {
    const u = user?.backButtonRedirect;
    if (u && String(u).trim() !== "") return String(u).trim();
    const base = process.env.REACT_APP_BASE_URL;
    if (base && String(base).trim() !== "") return String(base).replace(/\/$/, "");
    return "/login";
  }, [user?.backButtonRedirect]);

  useEffect(() => {
    const prev = user?.backButtonRedirect ?? null;
    const target = fromArrowRun ? "/rules" : safeExitUrl;
    dispatch(setBackButtonUrl(isMobileAppMode() ? null : target));
    return () => {
      if (prev) dispatch(setBackButtonUrl(prev));
    };
  }, [dispatch, user?.backButtonRedirect, safeExitUrl, fromArrowRun]);

  /** Browser “back” must not return to an in-app game route — send user to safe exit. */
  useEffect(() => {
    if (fromArrowRun) return undefined;
    const onPopState = () => {
      if (isMobileAppMode()) {
        mobileNavigateBack();
        return;
      }
      window.location.replace(safeExitUrl);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [safeExitUrl, fromArrowRun]);

  useEffect(() => {
    if (fromArrowRun || isFutureIndia) return;
    if (!themeData) return; // wait for theme to load before deciding to redirect
    const e = Number(state.energy);
    const t = Number(state.time);
    const h = Number(state.health);
    if (![e, t, h].every((x) => Number.isFinite(x))) {
      navigate("/rules", { replace: true });
    }
  }, [fromArrowRun, isFutureIndia, themeData, state.energy, state.time, state.health, navigate]);

 

  const canSubmitFeedback = Boolean(user?.userId && adminToken && stageId != null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const gameRoot = document.getElementById("game");
    if (gameRoot) gameRoot.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!feedbackOpen) return undefined;
    if (fromArrowRun) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setFeedbackOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [feedbackOpen, fromArrowRun]);

  const onRatingChange = (e) => {
    const v = Number(e.target.value);
    if (Number.isFinite(v)) setRating(v);
  };

  

  const toggleOn = themeData?.thankyou_toggle === "true" || themeData?.thankyou_toggle === true;
  const rawMsg = themeData?.thankyou_message;

  const thankYouMessageKeys = useMemo(() => {
    // Match Rules behavior: categories come from themeData.default_cat (dynamic per theme)
    const d = themeData?.default_cat;
    if (Array.isArray(d) && d.length) return d.map(String);

    // Fallback: derive from thankyou_message keys if default_cat missing
    const tm = themeData?.thankyou_message;
    if (tm && typeof tm === "object" && !Array.isArray(tm)) return Object.keys(tm);
    return [];
  }, [themeData?.default_cat, themeData?.thankyou_message]);

  const categoryThankYouLines = useMemo(() => {
    if (!thankYouMessageKeys.length) return [];
    // Support case differences between stored keys and runtime keys
    const msgObj = rawMsg && typeof rawMsg === "object" && !Array.isArray(rawMsg) ? rawMsg : {};
    const keyMap = new Map(Object.keys(msgObj).map((k) => [String(k).toUpperCase(), k]));
    return thankYouMessageKeys.map((k) => {
      const kk = String(k).toUpperCase();
      const realKey = keyMap.get(kk) ?? k;
      return themesetThankYouLine(msgObj?.[realKey]);
    });
  }, [rawMsg, thankYouMessageKeys]);

  /** When thankyou_toggle is off, single “normal” line: custom page text → first thankyou_message → default. */
  const primaryThankYouLine = useMemo(() => {
    const custom = String(themeData?.custom_text_thank_you_page || "").trim();
    if (custom) return custom;
    if (rawMsg && typeof rawMsg === "object") {
      if (thankYouMessageKeys.length) {
        for (const k of thankYouMessageKeys) {
          const line = themesetThankYouLine(rawMsg[k]);
          if (line) return line;
        }
      }
      const vals = Object.values(rawMsg).map(themesetThankYouLine).filter(Boolean);
      if (vals.length) return vals[0];
    }
    return "Thank you for participating!";
  }, [themeData?.custom_text_thank_you_page, rawMsg, thankYouMessageKeys]);

  const visuals = useMemo(() => thankYouThemeVisuals(themeData), [themeData]);

  const [arrowByCat, setArrowByCat] = useState({});
  const [stageRow, setStageRow] = useState(null);


  // Note: older DB fields like cat1_score/cat2_score/cat3_score are not reliable
  // for dynamic category sessions; prefer stage.arrowByCategory.

  const formatDur = (sec) => {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  const categoryIconUrls = useMemo(() => {
    const s3 = process.env.REACT_APP_S3_PATH || "";
    const icons = themeData?.cat_icons;
    return thankYouMessageKeys.map((cat) => {
      const v = icons?.[cat];
      if (v == null || v === "") return null;
      return typeof v === "string" && /^https?:\/\//i.test(v) ? v : `${s3}${v}`;
    });
  }, [themeData?.cat_icons, thankYouMessageKeys]);

  const pointsIconUrl = useMemo(() => s3PublicUrl(themeData?.points_icon), [themeData?.points_icon]);

  const heading =
    themeData?.custom_text_thank_you_page != null ? String(themeData.custom_text_thank_you_page) : "";
  const textColor = visuals.textColor;
  const boxTextColor = visuals.boxTextColor;
  const accent = visuals.textColor;
  const tooltipPct = ((rating - 1) / 4) * 100;
  const fillPct = ((rating - 1) / 4) * 100;
  const isMaxRating = rating >= 5;
  /** Slider thumb: happy faces only (1–5), no sad emoji. */
  const ratingHappyEmojis = ["🙂", "😊", "😄", "😁", "🤩"];
  const thumbEmoji = ratingHappyEmojis[Math.min(4, Math.max(0, rating - 1))];

  const showTopThankYouBanner = !toggleOn || (toggleOn && thankYouMessageKeys.length < 1);
  const topThankYouBannerText = !toggleOn
    ? primaryThankYouLine
    : heading.trim() || primaryThankYouLine;

  if (isFutureIndia) {
    // FutureThankYou needs reportData and stagesData that only FutureGame has.
    // Send the user to /main where FutureGame will detect all stages complete
    // and render FutureThankYou with full data.
    navigate('/main', { replace: true });
    return null;
  }

  return (
    <div
      className={`thank-you-container${toggleOn ? " thank-you-container--toggle-on" : ""}`}
      style={{
        color: textColor,
        "--thank-you-text": textColor,
        "--thank-you-box-text": boxTextColor,
        "--thank-you-box-bg": visuals.cardBg,
        "--thank-you-rating-accent": accent,
      }}
    >
      {showTopThankYouBanner && topThankYouBannerText && (
        <div className="thank-you-text thank-you-heading" style={{ color: textColor }}>
          {topThankYouBannerText}
        </div>
      )}

      {fromArrowRun && toggleOn && thankYouMessageKeys.length > 0 && (
        <ul
          className="thank-you-arrow-summary"
          style={{ color: textColor, listStyle: "none", padding: 0, margin: "1rem auto", maxWidth: 440 }}
        >
          
        </ul>
      )}

      {toggleOn && thankYouMessageKeys.length > 0 && (
        <div className="thank-you-meter-cards" role="list">
          {thankYouMessageKeys.map((categoryKey, idx) => {
            const label = formatCategoryHeading(categoryKey);
            const body = categoryThankYouLines[idx];
            const iconSrc = categoryIconUrls[idx];
            const fallbacks = ["📊", "🍎", "✨"];
            const iconFallback = fallbacks[idx] ?? "•";
            const bodyTrim = String(body || "").trim();
            const row = arrowByCat[String(categoryKey).toUpperCase()];
            const scoreVal =
              row && Number.isFinite(Number(row.score))
                ? Number(row.score)
                : 0;
            return (
              <section
                key={categoryKey}
                className="thank-you-meter-card"
                role="listitem"
                style={{
                  background: visuals.popupBg,
                  color: textColor,
                  borderColor: textColor,
                }}
              >
                <div className="thank-you-meter-card-icon" aria-hidden="true">
                  <ThemeIconImg src={iconSrc} fallback={iconFallback} alt="" />
                </div>
                <h3 className="thank-you-meter-card-heading" style={{ color: textColor }}>
                  {label}
                </h3>
                <div className="thank-you-meter-card-score" style={{ color: textColor }}>
                  {pointsIconUrl ? (
                    <img src={pointsIconUrl} alt="" className="thank-you-points-icon" aria-hidden />
                  ) : null}
                  <span className="thank-you-meter-card-score-text">
                     <strong>{scoreVal}</strong>
                  </span>
                </div>
                {bodyTrim ? (
                  <p className="thank-you-meter-card-body" style={{ color: boxTextColor }}>
                    {bodyTrim}
                  </p>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      <div className="thank-you-feedback-trigger-wrap">
        {feedbackSubmitted ? (
          <p className="thank-you-feedback-thanks" style={{ color: textColor }}>
            Thank you for your feedback.
          </p>
        ) : (
          <button
            type="button"
            className="thank-you-rate-btn"
            style={{
              background: visuals.cardBg,
              color: textColor,
              borderColor: textColor,
            }}
            onClick={() => setFeedbackOpen(true)}
          >
            {fromArrowRun ? "Share your experience" : "Share feedback"}
          </button>
        )}
      </div>

 
    </div>
  );
}

export default ThankYou;
